import React, { useEffect, useState } from "react";
import { FormControlNames } from "../../../../utils/formBuilderUtils";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Swal from "sweetalert2";
import CameraWithCrop from "../CameraWithCrop";
import CameraCaptureWithResizableCrop from "../CameraCaptureWithResizableCrop";
import DatePickerCustom from "../DatePickerCustom";
import SimpleDatePicker from "../SimpleDatePicker";
import capitalizeWords from "../../../../utils/firstLetterCapital";
import heic2any from "heic2any";
import { CircularProgress } from "@mui/material";

const RenderItemForUserForm = (props) => {
  const { item, value, onChange, isConvertingFile, setIsConvertingFile } = props;
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [error, setError] = useState(false);
  // Add the missing state for showCamera
  const [showCamera, setShowCamera] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // To hold the current preview image
  const [previewSource, setPreviewSource] = useState(null); // "camera" or "upload"

  const handleChange = (event) => {
    const newValue = event?.target?.value ?? "";
    onChange(item.id, capitalizeWords(newValue));
  };

  const dropdownHandleChange = (event) => {
    const newValue = event?.target?.value ?? "";
    onChange(item.id, newValue);
  };

  // const handleDateChange = (event) => {
  //   onChange(item.id, event);
  // };

  const handleDateChange = (event) => {
    console.log("Selected date:", item.id, event);
    onChange(item.id, event, "date");
  };

  const shouldDisableDate = (date) => {
    const currentYear = new Date().getFullYear();
    return date.year() >= currentYear; // Disable dates from the current year
  };

  const handleChangeMobile = (e) => {
    const value = e.target?.value ?? "";
    if (value.length <= 10) {
      if (value.length === 10) {
        setError(false);
      } else {
        setError(true);
      }
      onChange(item.id, value); // Update value if valid or input is empty
    } else {
      setError(true);
    }
  };

  const handleCamera = (event) => {
    setShowCamera(true);
    setShowModal(true);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();

    try {
      if (ext === "heic" || ext === "heif") {
        setIsConvertingFile?.(true);

        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        });

        const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

        const convertedFile = new File(
          [jpegBlob],
          file.name.replace(/\.(heic|heif)$/i, ".jpg"),
          { type: "image/jpeg" }
        );

        setUploadedFileName(convertedFile.name);
        onChange(item.id, convertedFile, "file");

        const url = URL.createObjectURL(convertedFile);
        setPreviewUrl(url);
        setPreviewSource("upload");
        setFileUrl(url);
        return;
      }

      setUploadedFileName(file.name);
      onChange(item.id, file, "file");

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewSource("upload");
      setFileUrl(url);

      Swal.fire("File uploaded successfully!");
    } catch (error) {
      console.error("HEIC conversion error:", error);
      Swal.fire("Unable to preview this HEIC image in browser.", "", "error");
    } finally {
      setIsConvertingFile?.(false);
    }
  };

  const handleChecklistChange = (event, label) => {
    const updatedChecklist = value || [];
    if (event.target.checked) {
      updatedChecklist.push(label);
    } else {
      const index = updatedChecklist.indexOf(label);
      if (index > -1) updatedChecklist.splice(index, 1);
    }
    onChange(item.id, [...updatedChecklist]);
  };

  useEffect(() => {
    // Edit mode image preview
    if (typeof value === "string" && value.startsWith("http")) {
      setFileUrl(value);
      setPreviewUrl(value);
      setUploadedFileName("Previously uploaded image");
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  switch (item.controlName) {
    case FormControlNames.INPUTTEXTFIELD:
      return (
        <TextField
          type={item.dataType}
          fullWidth={false} // Disable fullWidth to apply custom width
          placeholder={item.placeholder}
          variant="outlined"
          required={item.required}
          value={value || ""}
          onChange={handleChange}
          sx={{
            width: "80%", // Set width to 80%
          }}
        />
      );
    case FormControlNames.PHONEFIELD:
      return (
        <TextField
          type="number" // Use "tel" to avoid showing spinner
          fullWidth={false} // Disable fullWidth to apply custom width
          placeholder={item.placeholder}
          variant="outlined"
          required={item.required}
          value={value || ""}
          onChange={handleChangeMobile}
          sx={{
            width: "80%", // Set width to 80%
            "& input[type=number]": {
              MozAppearance: "textfield", // Remove spinner in Firefox
            },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none", // Remove spinner in Chrome, Safari, Edge
              margin: 0,
            },
          }}
          inputProps={{
            inputMode: "numeric", // Use numeric input mode for mobile-friendly input
          }}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => {
            const allowedSpecialKeys = [
              "Backspace",
              "Delete",
              "ArrowLeft",
              "ArrowRight",
            ];
            if (!/[0-9]/.test(e.key) && !allowedSpecialKeys.includes(e.key)) {
              e.preventDefault();
            }
          }}
          error={error}
          helperText={error ? "Only 10 digit number allowed" : ""}
        />

        // <TextField
        //           type="number"  // Use "tel" for mobile number input
        //           fullWidth={false} // Disable fullWidth to apply custom width
        //           placeholder={item.placeholder}
        //           variant="outlined"
        //           required={item.required}
        //           value={value || ""}
        //           onChange={handleChangeMobile}
        //           sx={{
        //             width: "80%", // Set width to 80%
        //           }}
        //           onWheel={(e) => e.target.blur()}
        //           onKeyDown={(e) => {
        //             const allowedSpecialKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight",];
        //             if (!/[0-9]/.test(e.key) && !allowedSpecialKeys.includes(e.key)) {
        //               e.preventDefault();
        //             }
        //           }}
        //           error={error}
        //           helperText={error ? 'Only 10 digit no allowed' : ''}
        //         />
      );

    case FormControlNames.INPUTMULTILINE:
      return (
        <TextField
          type={item.dataType}
          fullWidth={false}
          multiline={true}
          minRows={item.rows}
          placeholder={item.placeholder}
          variant="outlined"
          required={item.required}
          value={value || ""}
          onChange={handleChange}
          sx={{
            width: "80%", // Set width to 80%
          }}
        />
      );

    case FormControlNames.CHECKBOX:
      return (
        <div className="m-t-20 p-l-0">
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                required={item.required}
                onChange={(e) => handleChange(e.target.checked)}
              />
            }
            style={{ marginLeft: "0px" }}
            label={item.placeholder}
          />
        </div>
      );

    case FormControlNames.RADIOGROUP:
      return (
        <FormControl>
          <RadioGroup
            name={item.controlName + item.id}
            value={value || ""}
            onChange={handleChange}
            row
          >
            {item.items?.map((i) => (
              <FormControlLabel
                required={item.required}
                value={i.value}
                key={i.value}
                control={<Radio />}
                label={i.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case FormControlNames.SELECTDROPDOWN:
      return (
        <FormControl style={{ minWidth: "80%" }}>
          <Select
            style={{ minWidth: "100%" }}
            variant="outlined"
            value={value ?? ""}
            required={item.required}
            onChange={dropdownHandleChange}
          >
            {item.items?.map((i) => (
              <MenuItem key={i.id} value={i.label}>
                {i.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case FormControlNames.DATEFIELD:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SimpleDatePicker
            value={value ?? null}
            onChange={handleDateChange}
            required={item.required}
            format="DD-MM-YYYY" // Set format to dd/mm/yyyy
            slotProps={{
              textField: { fullWidth: true, required: item.required },
            }}
            sx={{
              width: "80%", // Set width to 80%
            }}
            shouldDisableDate={shouldDisableDate}
          />
        </LocalizationProvider>
      );

    case FormControlNames.TIMEFIELD:
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker
            value={value || null}
            onChange={(time) => handleChange(time)}
            slotProps={{
              textField: { fullWidth: true, required: item.required },
            }}
            sx={{
              width: "80%", // Set width to 80%
            }}
          />
        </LocalizationProvider>
      );

    case FormControlNames.FILEUPLOAD:
      return (
        <>
          <input
            style={{ display: "none" }}
            id={item.controlName + item.id}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
          <label
            className="control-input-trigger-buttons"
            htmlFor={item.controlName + item.id}
          >
            <i className="fas fa-cloud-upload-alt"></i>
          </label>
          {uploadedFileName && (
            <>
              <p>Uploaded File: {uploadedFileName}</p>
              <img src={fileUrl} alt="Uploaded" width={150} height={150} />
            </>
          )}
        </>
      );

    case FormControlNames.IMAGEUPLOAD:
      return (
        <>
          <input
            style={{ display: "none" }}
            id={item.controlName + item.id}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
          <label
            className="control-input-trigger-buttons"
            htmlFor={item.controlName + item.id}
          >
            <i className="fas fa-cloud-upload-alt"></i>
          </label>
          {/* {uploadedFileName && (
            <>
              <p>Uploaded File: {uploadedFileName}</p>
              <img src={fileUrl} alt="Uploaded" width={150} height={150} />
            </>
          )} */}
          {isConvertingFile ? (
            <div >
              <CircularProgress size="30px" aria-label="Loading…" />
            </div>
          ) : (
            <>
              {(fileUrl || value) && (
                <>
                  {uploadedFileName && <p>Uploaded File: {uploadedFileName}</p>}
                  <img
                    src={fileUrl || value}
                    alt="Uploaded"
                    width={150}
                    height={150}
                    style={{ objectFit: "cover" }}
                  />
                </>
              )}
            </>
          )}
        </>
      );

    case FormControlNames.CAPTURECLICK:
      return (
        <div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCamera(true)}
          >
            Capture Image
          </button>

          {showCamera && (
            <CameraWithCrop
              onImageCapture={(file) => {
                if (!(file instanceof File)) {
                  Swal.fire("Captured image is invalid");
                  return;
                }

                onChange(item.id, file, "file");

                const preview = URL.createObjectURL(file);
                setPreviewUrl(preview);
                setPreviewSource("camera");
                setUploadedFileName(file.name || "captured-image.png");
                setFileUrl(preview);

                setShowCamera(false);
              }}
            />
          )}

          {previewUrl && (
            <div style={{ marginTop: 10 }}>
              <p>Captured Image:</p>
              <img
                src={previewUrl}
                alt="Captured"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                }}
              />
            </div>
          )}
        </div>
      );

    case FormControlNames.SCANCODE:
      return (
        <div>
          {/* Toggle Button */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowOptions(!showOptions)}
          >
            {showOptions ? "Close Upload" : "Open Upload"}
          </button>

          {/* File Upload and Camera Icons */}
          {showOptions && (
            <div>
              <p>Select Any one of below</p>
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                {/* File Upload Section */}
                <input
                  style={{ display: "none" }}
                  id={item.controlName + item.id}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <label
                  className="control-input-trigger-buttons"
                  htmlFor={item.controlName + item.id}
                >
                  <i className="fas fa-cloud-upload-alt"></i>
                </label>

                {/* Camera Section */}
                <label
                  className="control-input-trigger-buttons"
                  style={{ marginLeft: "20px" }}
                >
                  <i className="fas fa-camera" onClick={handleCamera} />
                </label>
              </div>
            </div>
          )}

          {/* Camera and Image Capture */}
          {showCamera && (
            <CameraWithCrop
              onImageCapture={(file) => {
                if (!(file instanceof File)) {
                  Swal.fire("Captured image is invalid");
                  return;
                }

                onChange(item.id, file, "file");

                const preview = URL.createObjectURL(file);
                setPreviewUrl(preview);
                setPreviewSource("camera");
                setUploadedFileName(file.name || "captured-image.png");
                setFileUrl(preview);

                setShowCamera(false);
              }}
            />
          )}

          {/* Preview Image - Conditional Rendering */}
          {previewUrl && (
            <div>
              <p>
                {previewSource === "upload"
                  ? "Uploaded File"
                  : "Captured Image"}{" "}
                Preview:
              </p>
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  width: "150px",
                  height: "150px",
                  objectFit: "cover",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}
        </div>
      );

    case FormControlNames.SIGNATURE:
      return (
        <>
          <input
            style={{ display: "none" }}
            id={item.controlName + item.id}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
          <label
            className="control-input-trigger-buttons"
            htmlFor={item.controlName + item.id}
          >
            <i className="fas fa-cloud-upload-alt"></i>
          </label>
          {uploadedFileName && (
            <>
              <p>Uploaded File: {uploadedFileName}</p>
              <img src={fileUrl} alt="Uploaded" width={150} height={150} />
            </>
          )}
        </>
      );

    case FormControlNames.TOGGLE:
      return (
        <Switch
          checked={!!value}
          required={item.required}
          onChange={(e) => onChange(item.id, e.target.checked)}
        />
      );

    case FormControlNames.CHECKLIST:
      return (
        <FormGroup>
          {item.items?.map((i) => (
            <FormControlLabel
              key={i.value}
              control={
                <Checkbox
                  checked={value?.includes(i.value) || false}
                  required={item.required}
                  onChange={(e) => {
                    const updatedValue = e.target.checked
                      ? [...(value || []), i.value]
                      : value.filter((val) => val !== i.value);
                    onChange(item.id, updatedValue);
                  }}
                />
              }
              label={i.label}
              style={{ marginLeft: "0px" }}
            />
          ))}
        </FormGroup>
      );

    default:
      return null;
  }
};

export default RenderItemForUserForm;
